import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { api, brl, formatDate } from "../lib/api";
import { useUser } from "../hooks/useUser";

let stripePromise: Promise<Stripe | null> | null = null;
function getStripePromise(): Promise<Stripe | null> {
  if (stripePromise) return stripePromise;
  stripePromise = api<{ stripe_publishable_key: string }>("/api/config/public").then((r) => {
    if (!r.ok) throw new Error("config_public_failed");
    return loadStripe(r.body.stripe_publishable_key);
  });
  return stripePromise;
}

type Session = {
  id: string;
  mode: string;
  payment_status: string;
  amount_total: number;
  currency: string;
  customer: string | null;
  subscription: string | null;
  payment_intent: string | null;
  metadata: Record<string, string>;
};

type CatalogProduct = {
  id: string;
  name: string;
  description: string;
  feature_key: string;
  amount_cents: number;
  currency: string;
  mode: "one-time" | "subscription";
  checkout_mode: "inline" | "hosted";
  recurring_interval?: "day" | "week" | "month" | "year";
  stripe_price_id?: string;
  // Upsells configurados via admin (vazio = fallback automático).
  upsells?: string[];
};

function intervalLabel(interval?: string): string {
  switch (interval) {
    case "month":
      return "/mês";
    case "year":
      return "/ano";
    case "week":
      return "/semana";
    case "day":
      return "/dia";
    default:
      return "";
  }
}

type Activation = {
  featureKey: string;
  status: string;
};

type UpsellState =
  | { kind: "idle" }
  | { kind: "running"; featureKey: string }
  | { kind: "authenticating"; featureKey: string }
  | { kind: "success"; featureKey: string }
  | { kind: "error"; featureKey: string; message: string };

const MAX_UPSELLS = 3;

export function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user } = useUser();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upsells, setUpsells] = useState<CatalogProduct[]>([]);
  const [upsellState, setUpsellState] = useState<UpsellState>({ kind: "idle" });
  // Cascata: index da oferta atual. Avança ao clicar "Não, obrigado" ou "Pular".
  // Padrão Hotmart — mostra uma oferta por vez em vez de empilhar todas.
  const [currentUpsellIndex, setCurrentUpsellIndex] = useState(0);
  // Impression id retornado pelo POST /api/upsell-offers. Usado pra PATCH a
  // outcome (accepted/declined/errored) e medir taxa de conversão por upsell.
  const [currentImpressionId, setCurrentImpressionId] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (!sessionId) {
      setError("session_id não veio na URL");
      setLoading(false);
      return;
    }
    api<Session>(`/api/checkout-sessions/${sessionId}`).then((r) => {
      if (r.ok) setSession(r.body);
      else setError(`Falha ao carregar session (HTTP ${r.status})`);
      setLoading(false);
    });
  }, [sessionId]);

  // Carrega upsells: hosted que user não tem ativo, ≠ do produto recém-comprado.
  // Inclui one-time E subscription (sub upsell exige stripe_price_id pra Stripe
  // criar sub off-session). Roda só depois da session carregar.
  //
  // Se o produto comprado tem `upsells` configurados via admin, narrowing pra essa
  // lista (preservando a ordem definida — exibição em cascata respeita position).
  // Lista vazia → fallback automático (todos elegíveis), mantendo POC funcional
  // antes do admin configurar.
  useEffect(() => {
    if (!user || !session) return;
    const justBoughtKey = session.metadata?.feature_key;
    Promise.all([
      api<{ data: CatalogProduct[] }>("/api/catalog"),
      api<{ data: Activation[] }>(`/api/users/${user.id}/activations`),
    ]).then(([catalogRes, actsRes]) => {
      if (!catalogRes.ok || !actsRes.ok) return;
      const activeFeatures = new Set(
        actsRes.body.data
          .filter((a) => a.status === "active")
          .map((a) => a.featureKey)
      );
      const eligible = (p: CatalogProduct) =>
        p.checkout_mode === "hosted" &&
        p.feature_key !== justBoughtKey &&
        !activeFeatures.has(p.feature_key) &&
        (p.mode === "one-time" ||
          (p.mode === "subscription" && !!p.stripe_price_id));

      const parent = catalogRes.body.data.find(
        (p) => p.feature_key === justBoughtKey
      );
      const configuredKeys = parent?.upsells ?? [];

      let candidates: CatalogProduct[];
      if (configuredKeys.length > 0) {
        // Mantém a ordem do admin (position) — cascata respeita essa sequência.
        const byKey = new Map(
          catalogRes.body.data.map((p) => [p.feature_key, p])
        );
        candidates = configuredKeys
          .map((k) => byKey.get(k))
          .filter((p): p is CatalogProduct => !!p && eligible(p));
      } else {
        candidates = catalogRes.body.data.filter(eligible);
      }
      setUpsells(candidates.slice(0, MAX_UPSELLS));
    });
  }, [user, session]);

  // Registra impression toda vez que cascata avança pra uma oferta nova. Falha
  // silenciosa — tracking não pode travar UX.
  useEffect(() => {
    if (!user || !session) return;
    const p = upsells[currentUpsellIndex];
    if (!p) {
      setCurrentImpressionId(null);
      return;
    }
    let cancelled = false;
    api<{ id: number }>("/api/upsell-offers", {
      method: "POST",
      body: JSON.stringify({
        user_id: user.id,
        parent_feature_key: session.metadata?.feature_key ?? undefined,
        offer_feature_key: p.feature_key,
        session_id: session.id,
      }),
    }).then((r) => {
      if (cancelled) return;
      if (r.ok) setCurrentImpressionId(r.body.id);
      else setCurrentImpressionId(null);
    });
    return () => {
      cancelled = true;
    };
  }, [user, session, upsells, currentUpsellIndex]);

  // Marca o outcome da impression atual (accepted / declined / errored).
  // Best-effort — não bloqueia o fluxo se a request falhar.
  const recordOutcome = (outcome: "accepted" | "declined" | "errored") => {
    if (currentImpressionId == null) return;
    void api(`/api/upsell-offers/${currentImpressionId}`, {
      method: "PATCH",
      body: JSON.stringify({ outcome }),
    });
  };

  const handleUpsell = async (p: CatalogProduct) => {
    if (!user) return;
    // Consentimento explícito pra upsell recorrente. POC usa window.confirm;
    // produção deve ter modal com texto legal (recorrência, valor, cancelamento).
    if (p.mode === "subscription") {
      const valor = brl(p.amount_cents, p.currency);
      const intervalo = intervalLabel(p.recurring_interval);
      const ok = window.confirm(
        `Você está autorizando uma cobrança recorrente de ${valor}${intervalo} no cartão que acabou de usar.\n\nPode cancelar a qualquer momento em Minha Conta.\n\nConfirmar assinatura?`
      );
      if (!ok) return;
    }
    setUpsellState({ kind: "running", featureKey: p.feature_key });
    const r = await api<{
      payment_intent_id?: string;
      subscription_id?: string;
      status: string;
      requires_action?: boolean;
      client_secret?: string;
      payment_method?: string;
    }>("/api/upsell-charges", {
      method: "POST",
      body: JSON.stringify({ user_id: user.id, feature_key: p.feature_key }),
    });
    if (!r.ok) {
      recordOutcome("errored");
      setUpsellState({
        kind: "error",
        featureKey: p.feature_key,
        message:
          (r.body as any)?.message ??
          (r.body as any)?.error ??
          `Falha (HTTP ${r.status})`,
      });
      return;
    }
    // 3DS: cartão exige challenge no off-session. PI ficou em
    // requires_payment_method (PM desanexado). Front re-confirma on-session
    // com o mesmo PM, dispara modal 3DS; webhook ativa a feature após sucesso.
    if (r.body.requires_action && r.body.client_secret && r.body.payment_method) {
      setUpsellState({ kind: "authenticating", featureKey: p.feature_key });
      try {
        const stripe = await getStripePromise();
        if (!stripe) throw new Error("stripe_js_load_failed");
        const { paymentIntent, error } = await stripe.confirmCardPayment(
          r.body.client_secret,
          { payment_method: r.body.payment_method }
        );
        if (error) {
          recordOutcome("errored");
          setUpsellState({
            kind: "error",
            featureKey: p.feature_key,
            message: error.message ?? "Autenticação 3DS falhou",
          });
          return;
        }
        if (paymentIntent?.status !== "succeeded") {
          recordOutcome("errored");
          setUpsellState({
            kind: "error",
            featureKey: p.feature_key,
            message: `Pagamento não confirmado (status ${paymentIntent?.status ?? "desconhecido"})`,
          });
          return;
        }
      } catch (err: any) {
        recordOutcome("errored");
        setUpsellState({
          kind: "error",
          featureKey: p.feature_key,
          message: err.message ?? "Erro inesperado no 3DS",
        });
        return;
      }
    }
    recordOutcome("accepted");
    setUpsellState({ kind: "success", featureKey: p.feature_key });
    setTimeout(() => navigate("/conta"), 2000);
  };

  const isAsync = session?.payment_status === "unpaid";
  // Upsell pós-compra funciona tanto após one-time (mode=payment) quanto após
  // assinatura (mode=subscription). Em ambos os casos o cartão fica salvo
  // (sub mode salva por default; payment usa setup_future_usage="off_session"),
  // o que permite cobrança off-session imediata.
  const currentUpsell: CatalogProduct | undefined = upsells[currentUpsellIndex];
  const canShowUpsells =
    !isAsync &&
    (session?.mode === "payment" || session?.mode === "subscription") &&
    !!currentUpsell;

  const handleSkipUpsell = () => {
    recordOutcome("declined");
    setUpsellState({ kind: "idle" });
    setCurrentUpsellIndex((i) => i + 1);
  };

  return (
    <div className="page-narrow">
      <div className="result-card">
        <h1>{isAsync ? "Aguardando pagamento" : "Pagamento processado"}</h1>
        {loading ? (
          <p>Carregando dados da sessão...</p>
        ) : error ? (
          <p className="error">{error}</p>
        ) : session ? (
          <>
            <p className="hint">
              {isAsync ? (
                <>
                  Você escolheu <strong>Pix ou Boleto</strong>. O Stripe gerou o
                  pagamento mas o dinheiro ainda não caiu. Sua feature só é
                  ativada quando recebermos o webhook{" "}
                  <code>payment_intent.succeeded</code>.
                  <br />
                  <small>Pix: minutos. Boleto: 1-3 dias úteis após pagar.</small>
                </>
              ) : (
                <>
                  O Stripe Checkout retornou. O webhook
                  {session.mode === "subscription"
                    ? " invoice.paid"
                    : " payment_intent.succeeded"}{" "}
                  vai disparar em segundos e ativar sua feature.
                </>
              )}
            </p>
            <dl className="result-list">
              <dt>Modo</dt>
              <dd>{session.mode}</dd>
              <dt>Status</dt>
              <dd>{session.payment_status}</dd>
              <dt>Valor</dt>
              <dd>{brl(session.amount_total, session.currency)}</dd>
              {session.subscription && (
                <>
                  <dt>Subscription</dt>
                  <dd><code>{session.subscription}</code></dd>
                </>
              )}
              {session.payment_intent && (
                <>
                  <dt>PaymentIntent</dt>
                  <dd><code>{session.payment_intent}</code></dd>
                </>
              )}
              <dt>Session ID</dt>
              <dd><code>{session.id}</code></dd>
              <dt>Confirmado em</dt>
              <dd>{formatDate(new Date().toISOString())}</dd>
            </dl>
          </>
        ) : null}

        {canShowUpsells &&
          (() => {
            const p = currentUpsell!;
            const running =
              upsellState.kind === "running" &&
              upsellState.featureKey === p.feature_key;
            const authenticating =
              upsellState.kind === "authenticating" &&
              upsellState.featureKey === p.feature_key;
            const success =
              upsellState.kind === "success" &&
              upsellState.featureKey === p.feature_key;
            const errored =
              upsellState.kind === "error" &&
              upsellState.featureKey === p.feature_key;
            const isSub = p.mode === "subscription";
            const isBusy =
              upsellState.kind === "running" ||
              upsellState.kind === "authenticating";
            const priceLabel = `${brl(p.amount_cents, p.currency)}${
              isSub ? intervalLabel(p.recurring_interval) : ""
            }`;
            const hasNext = currentUpsellIndex < upsells.length - 1;

            return (
              <div className="upsell-block">
                <h2>
                  Aproveite e ative agora{" "}
                  <span
                    className="muted"
                    style={{ fontWeight: "normal", fontSize: "0.7em" }}
                  >
                    (oferta {currentUpsellIndex + 1} de {upsells.length})
                  </span>
                </h2>
                <p className="hint">
                  Cobrança one-click no cartão que você acabou de usar.
                </p>
                <div className="upsell-card">
                  <div className="upsell-info">
                    <strong>
                      {p.name}
                      {isSub && (
                        <span
                          className="tag tag-subscription"
                          style={{ marginLeft: "0.5em" }}
                        >
                          Recorrente
                        </span>
                      )}
                    </strong>
                    <p className="muted">{p.description}</p>
                    {isSub && (
                      <p className="muted" style={{ fontSize: "0.85em" }}>
                        Cobrança {intervalLabel(p.recurring_interval).replace("/", "")}
                        ; cancele a qualquer momento em Minha Conta.
                      </p>
                    )}
                  </div>
                  <div className="upsell-action">
                    {success ? (
                      <span className="upsell-status status-success">
                        {isSub ? "Assinado!" : "Cobrado!"} Redirecionando…
                      </span>
                    ) : (
                      <button
                        className="primary-btn"
                        onClick={() => handleUpsell(p)}
                        disabled={isBusy}
                      >
                        {running
                          ? isSub
                            ? "Assinando..."
                            : "Cobrando..."
                          : authenticating
                          ? "Confirmando 3DS..."
                          : errored
                          ? "Tentar de novo"
                          : `${isSub ? "Assinar por" : "Ativar por"} ${priceLabel}`}
                      </button>
                    )}
                  </div>
                </div>
                {errored && (
                  <p
                    className="upsell-status status-error"
                    style={{ marginTop: "0.5em" }}
                  >
                    {upsellState.message}
                  </p>
                )}
                {!success && (
                  <div
                    className="upsell-skip"
                    style={{ marginTop: "0.75em", textAlign: "right" }}
                  >
                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={handleSkipUpsell}
                      disabled={isBusy}
                    >
                      {hasNext ? "Não, obrigado — próxima oferta" : "Não, obrigado"}
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

        <div className="actions">
          <Link to="/conta" className="primary-link">
            Ver minhas features →
          </Link>
          <Link to="/loja" className="secondary-link">
            Voltar pra loja
          </Link>
        </div>
      </div>
    </div>
  );
}

export function Cancel() {
  return (
    <div className="page-narrow">
      <div className="result-card">
        <h1>Pagamento cancelado</h1>
        <p className="hint">Você cancelou no Stripe Checkout. Nada foi cobrado.</p>
        <div className="actions">
          <Link to="/loja" className="primary-link">
            Voltar pra loja
          </Link>
        </div>
      </div>
    </div>
  );
}
